import { z } from "zod";

const timestamp = z.string().datetime({ offset: true });
export const keySchema = z.object({ id:z.string(), name:z.string(), prefix:z.string(), suffix:z.string(), serviceId:z.string().nullable().optional(), scopes:z.array(z.string()), createdAt:timestamp, lastUsedAt:timestamp.nullable().optional(), expiresAt:timestamp.nullable().optional(), status:z.enum(["active","revoked","expired"]) });
export const usageSchema = z.object({ at:timestamp, requests:z.number().nonnegative(), errors:z.number().nonnegative() });
export const limitSchema = z.object({ id:z.string(), serviceId:z.string(), name:z.string(), allowed:z.number().nonnegative().nullable(), used:z.number().nonnegative().nullable(), window:z.string(), resetsAt:timestamp.nullable().optional(), behavior:z.string().nullable().optional() });
export const eventSchema = z.object({ id:z.string(), type:z.string(), severity:z.enum(["info","success","warning","critical"]), serviceId:z.string().nullable().optional(), title:z.string(), description:z.string(), createdAt:timestamp, requestId:z.string().nullable().optional(), metadata:z.record(z.string(),z.unknown()).optional() });
export const notificationSchema = z.object({ id:z.string(), type:z.string(), category:z.enum(["security","api","account","service","product"]), severity:z.enum(["info","success","warning","critical"]), title:z.string(), body:z.string(), href:z.string().nullable().optional(), attachmentUrl:z.string().url().nullable().optional(), createdAt:timestamp, readAt:timestamp.nullable().optional(), expiresAt:timestamp.nullable().optional(), metadata:z.record(z.string(),z.unknown()).optional(), dedupeKey:z.string().nullable().optional() });
export const pushStatusSchema = z.object({ enabled:z.boolean(), activeDeviceCount:z.number().int().nonnegative(), webEnabled:z.boolean(), iosEnabled:z.boolean(), androidEnabled:z.boolean(), currentBrowserBound:z.boolean().optional() });
export const deviceSchema = z.object({ id:z.string(), platform:z.enum(["web","ios","android"]), label:z.string(), browser:z.string().optional(), os:z.string().optional(), deviceType:z.string().optional(), lastSeenAt:timestamp, active:z.boolean(), current:z.boolean().optional() });

const keyPermissionSchema = z.object({ appId: z.string(), scopes: z.array(z.string()) });
export const keysApiKeySchema = z.object({
  id: z.string(),
  name: z.string().nullable().optional(),
  status: z.enum(["active", "revoked", "expired"]),
  permissions: z.array(keyPermissionSchema).optional().default([]),
  appId: z.string().optional(),
  scopes: z.array(z.string()).optional(),
  created_at: z.number().int(),
  last_used_at: z.number().int().nullable().optional(),
  expires_at: z.number().int().nullable().optional()
}).transform(raw => {
  const permissions = raw.permissions.length ? raw.permissions : raw.appId ? [{ appId: raw.appId, scopes: raw.scopes ?? [] }] : [];
  return {
    id: raw.id,
    name: raw.name ?? "Unnamed key",
    serviceId: permissions.length === 1 ? permissions[0].appId : null,
    scopes: [...new Set(permissions.flatMap(permission => permission.scopes))],
    createdAt: new Date(raw.created_at * 1000).toISOString(),
    lastUsedAt: raw.last_used_at == null ? null : new Date(raw.last_used_at * 1000).toISOString(),
    expiresAt: raw.expires_at == null ? null : new Date(raw.expires_at * 1000).toISOString(),
    status: raw.status
  };
});

export const keysApiLimitSchema = z.object({
  appId: z.string(),
  limitType: z.string(),
  limitValue: z.number().nonnegative(),
  currentValue: z.number().nonnegative(),
  remaining: z.number().nonnegative(),
  maximumValue: z.number().nonnegative(),
  daily: z.boolean()
}).transform(raw => ({
  id: `${raw.appId}:${raw.limitType}`,
  serviceId: raw.appId,
  name: raw.limitType,
  allowed: raw.limitValue,
  used: raw.currentValue,
  window: raw.daily ? "daily" : "current",
  behavior: raw.maximumValue > raw.limitValue ? `maximum ${raw.maximumValue}` : null
}));

export const serviceHealthSchema = z.object({
  services: z.record(z.string(), z.enum(["operational", "degraded", "unavailable", "unknown"]))
});

export const hmeAliasSchema = z.object({
  id: z.string(), alias: z.string(), domain: z.string(), forward_to: z.string(), label: z.string().nullable().optional(),
  status: z.enum(["active", "disabled"]), emails_forwarded: z.number().int().nonnegative(), last_forwarded_at: z.number().int().nullable().optional(), created_at: z.number().int()
}).transform(raw => ({
  id: raw.id, email: `${raw.alias}@${raw.domain}`, forwardTo: raw.forward_to, label: raw.label, status: raw.status,
  emailsForwarded: raw.emails_forwarded, lastForwardedAt: raw.last_forwarded_at == null ? null : new Date(raw.last_forwarded_at * 1000).toISOString(), createdAt: new Date(raw.created_at * 1000).toISOString()
}));

export const hmeDestinationSchema = z.object({
  email: z.string(), status: z.string(), verified_at: z.number().int().nullable().optional(), last_checked_at: z.number().int().nullable().optional(), confirmationEmailSent: z.boolean().optional()
}).transform(raw => ({
  email: raw.email, status: (raw.status === "verified" || raw.status === "pending" ? raw.status : "unknown") as "verified" | "pending" | "unknown",
  verifiedAt: raw.verified_at == null ? null : new Date(raw.verified_at * 1000).toISOString(), lastCheckedAt: raw.last_checked_at == null ? null : new Date(raw.last_checked_at * 1000).toISOString(), confirmationEmailSent: raw.confirmationEmailSent
}));

export const verifyResourceSchema = z.object({
  id: z.string(), resource_type: z.string(), value: z.string(), method: z.enum(["dns_txt", "magic_link", "verified_zone"]),
  verified_at: z.number().int().nullable().optional(), reverify_due_at: z.number().int().nullable().optional(), grace_expires_at: z.number().int().nullable().optional()
}).transform(raw => ({
  id: raw.id, resourceType: raw.resource_type, value: raw.value, method: raw.method,
  verifiedAt: raw.verified_at == null ? null : new Date(raw.verified_at * 1000).toISOString(),
  reverifyDueAt: raw.reverify_due_at == null ? null : new Date(raw.reverify_due_at * 1000).toISOString(),
  graceExpiresAt: raw.grace_expires_at == null ? null : new Date(raw.grace_expires_at * 1000).toISOString()
}));

export const mailTemplateSchema = z.object({
  id: z.string(), name: z.string(), url: z.string(), variables: z.array(z.string()).optional().default([]), created_at: z.number().int().nullable().optional()
}).transform(raw => ({
  id: raw.id,
  name: raw.name,
  url: raw.url,
  variables: raw.variables,
  createdAt: raw.created_at == null ? null : new Date(raw.created_at * 1000).toISOString()
}));

export const dnsZoneSchema = z.object({
  id:z.string(), name:z.string(), catalogZone:z.string(), status:z.enum(["provisioning","active","error","deleting"]),
  dnssecStatus:z.enum(["unsigned","signing","signed","unsigning","unknown"]), recordCount:z.number().int().nonnegative(), createdAt:z.number(), updatedAt:z.number()
});

export const dnsRecordSchema = z.object({
  id:z.string(), zoneId:z.string(), name:z.string(), type:z.enum(["A","AAAA","NS","CNAME","MX","TXT","SRV","CAA","HTTPS","DS","DNSKEY","SMIMEA","URI"]),
  ttl:z.number().int(), data:z.record(z.string(),z.unknown()), status:z.enum(["provisioning","active","error"]), createdAt:z.number(), updatedAt:z.number()
});
